import { faCircleExclamation, faPartyHorn } from '@fortawesome/pro-light-svg-icons'
import { useCallback } from 'react'
import TableSchedule, { ScheduleTimeTy } from '../../../../../../../../specificComponents/WCal/schedule/TableSchedule'
import TwinIcon from '../../../../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../../../../baseComponents/TwinTrans'
import Checkbox from '../../../../../../../../forms/Checkbox'
import { RectangularInputHourUnlimited } from '../../../../../../../../forms/Input/InputHour/InputHourUnlimited'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import useDictDays from '../../../../../../../../utils/hooks/wCalTurn/useDictDays'
import { WCalTurnScheduleDictTY, WCalTurnSingleSchedule } from '../../types'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { RenderScheduleJoin } from '../../../../../../../../specificComponents/WCal/WCalTurns/RenderSchedule'

interface ScheduleDayBasicProps extends METurnSingleDayLogicProps {
    mySchedule: WCalTurnSingleSchedule
}

interface ScheduleDayProps extends ScheduleDayBasicProps {
    active: boolean
    punchInWithWorkingCenter: boolean
}

const METurnSingleDay: React.FC<ScheduleDayProps> = ({ mySchedule, active, ...rest }) => {
    const { onChangeScheduleTime } = useMETurnSingleDayLogic({ ...rest })
    const Component = componentScheduleMessageDecider(mySchedule)
    return (
        <div className='schedule_day'>
            <HeaderScheduleDay {...{ active, mySchedule, ...rest }} />
            {active && mySchedule.schedule?.workingTime?.length ?
                <div>
                    <ScheduleTypeMessage mySchedule={mySchedule}/>
                    <TableSchedule errors={mySchedule.schedule?.errors} onChange={onChangeScheduleTime} scheduleTime={mySchedule?.schedule?.workingTime} />
                </div>
                : <Component mySchedule={mySchedule} />
            }
        </div>
    )
}

interface METurnSingleDayLogicProps {
    setMySchedules: React.Dispatch<React.SetStateAction<{ [key: string]: WCalTurnScheduleDictTY }>>
    workingCenter: string
    day: string
    baseJourney: WCalTurnScheduleDictTY
}

const useMETurnSingleDayLogic = ({ setMySchedules, day, workingCenter, baseJourney }: METurnSingleDayLogicProps) => {
    const onChangeScheduleTime = useCallback((scheduleTime: ScheduleTimeTy) => {
        setMySchedules((old) => {
            if (!old) {
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld[day][workingCenter].schedule.workingTime = scheduleTime
            if (!scheduleTime.length) {
                copyOld[day][workingCenter].schedule.flexible = false
            }
            return { ...copyOld }
        })
    }, [day, setMySchedules, workingCenter])

    return { onChangeScheduleTime }
}

const componentScheduleMessageDecider = (mySchedule: WCalTurnSingleSchedule) => {
    const type = mySchedule?.type
    const isHalfDay = mySchedule?.isHalfDay
    const myWorkingTime = mySchedule?.schedule?.workingTime
    const isBaseTurn = mySchedule.isBaseTurn
    const isTurnDay = mySchedule?.isTurnDay
    const originalOvertime = mySchedule?.originalOvertime
    if ((type === 'holidayDay' && !isHalfDay) || type === 'freeDay' || (isTurnDay && (!myWorkingTime || !myWorkingTime?.length) && !isBaseTurn)) {
        return FreeDayMessage
    } else if (mySchedule?.absenceWorkingTime?.length || (type === 'holidayDay' && isHalfDay)) {
        return ScheduleMessage
    } else if ((type === 'noWorkDay' && !isTurnDay) || (!mySchedule?.originalJourney && !myWorkingTime?.length && !isTurnDay && !originalOvertime?.length)) {
        return NoTurnDayMessage
    } else if (isBaseTurn && type === 'normalDay' && !mySchedule?.originalJourney && !originalOvertime?.length) {
        return NoTurnDayMessage
    }
    return ScheduleMessage
}

const NoTurnDayMessage: React.FC = () => {
    return (
        <div className='border border-gray-D6 rounded-8 flex flex-col py-10'>
            <div className='text-red-BA flex mt-5'>
                <TwinIcon className='mx-10 h-20 w-20' icon={faCircleExclamation}></TwinIcon>
                <div className='regular16'>
                    <TwinTrans transKey='noSpecifiedTurnDay'>Sin turno especificado</TwinTrans>
                </div>
            </div>
        </div>
    )
}
interface ScheduleMessageProps {
    mySchedule: WCalTurnSingleSchedule
}
const ScheduleMessage: React.FC<ScheduleMessageProps> = ({ mySchedule }) => {
    return (
        <div className='border border-gray-D6 rounded-8 flex flex-col py-10'>
            <ScheduleTypeMessage mySchedule={mySchedule} />
        </div>
    )
}

interface ScheduleTypeMessageProps {
    mySchedule: WCalTurnSingleSchedule
}

const ScheduleTypeMessage: React.FC<ScheduleTypeMessageProps> = ({ mySchedule }) => {
    const renderThis: JSX.Element[] = []
    const workingTime = mySchedule?.originalJourney?.workingTime || (!mySchedule.TurnId  && mySchedule.type !== 'noWorkDay' ? mySchedule?.schedule?.workingTime : [])
    if (mySchedule?.type === 'freeDay') {
        renderThis.push(<HalfFreeDayMessage key={'freeDay'}/>)
    }
    if (mySchedule?.isHalfDay) {
        renderThis.push(<HolidayHalfDayMessage key={'halfDayHoliday'} />)
    }
    if (workingTime && workingTime.length && mySchedule?.type !== 'freeDay') {
        if (mySchedule?.originalJourney?.workingTime) {
            renderThis.push(
                <ScheduleDayMessage workingTime={mySchedule?.originalJourney?.workingTime} type={mySchedule?.type} key={'scheduleDay'} />
            )
        }
    }
    if (mySchedule?.originalOvertime && mySchedule?.originalOvertime.length) {
        renderThis.push(<ScheduleWithOvertimeMessage mySchedule={mySchedule} key={'overtimeDay'} />)
    }
    if (mySchedule.type === 'absenceDay' || mySchedule.type === 'absenceJustifiedDay' || mySchedule?.absenceWorkingTime?.length) {
        renderThis.push(<ScheduleAbsenceDayMessage absenceTime={mySchedule?.absenceWorkingTime || []} key={mySchedule.type} />)
    }
    return <div>{renderThis}</div>
}

const HalfFreeDayMessage: React.FC = () => {
    return (
        <div className='flex text-green-43 ml-10 '>
            <TwinIcon className='h-24 mr-8' icon={faPartyHorn} />
            <div className='regular16'><TwinTrans transKey='freeDay'>Día festivo</TwinTrans></div>
        </div>
    )
}

const HolidayHalfDayMessage: React.FC = () => {
    return (
        <div className='flex text-green-43 ml-10 '>
            <TwinIcon className='h-24 mr-8' icon={faPartyHorn} />
            <div className='regular16'><TwinTrans transKey='halfHolidayDay'>Medio día de vacaciones</TwinTrans></div>
        </div>
    )
}

interface ScheduleDayMessageProps {
    workingTime: number[][]
    type: string
}
const ScheduleDayMessage: React.FC<ScheduleDayMessageProps> = ({ workingTime, type}) => {
    const { t } = useTwinTranslation()
    const scheduleText = t('schedule', 'Horario')
    const dictScheduleTypes: TwinDictionary = {
        'normalDay': { text: t('baseSchedule', 'Horario base') },
        'specialDay': { text: t('specialDay', 'Día especial') },
    }
    return (
        <ScheduleTypeMessageRow title={dictScheduleTypes[type]?.text || scheduleText}>
            <RenderScheduleJoin workingTime={workingTime}/>
        </ScheduleTypeMessageRow>
    )
}

interface ScheduleWithOvertimeMessageProps {
    mySchedule: WCalTurnSingleSchedule
}
const ScheduleWithOvertimeMessage: React.FC<ScheduleWithOvertimeMessageProps> = ({ mySchedule }) => {
    const { t } = useTwinTranslation()
    return (
        <ScheduleTypeMessageRow title={t('overtime', 'Horas extra')}>
            <RenderScheduleJoin workingTime={mySchedule?.originalOvertime || []} />
        </ScheduleTypeMessageRow>
    )
}

interface ScheduleAbsenceDayMessageProps {
    absenceTime: number[][]
}
const ScheduleAbsenceDayMessage: React.FC<ScheduleAbsenceDayMessageProps> = ({ absenceTime}) => {
    const { t } = useTwinTranslation()
    return (
        <ScheduleTypeMessageRow title={t('workLeave', 'Baja laboral')}>
            <RenderScheduleJoin workingTime={absenceTime} />
        </ScheduleTypeMessageRow>
    )
}

interface ScheduleTypeMessageRowProps {
    title: string,
    children?: React.ReactNode
    className?: string
}

const ScheduleTypeMessageRow: React.FC<ScheduleTypeMessageRowProps> = ({ title, className, children }) => {
    return (
        <div className={'flex py-5' + (className || '')}>
            <span className='regular16  ml-10'>{title}: </span>
            <div className='regular16 mx-10 flex gap-5'>
                {children}
            </div>
        </div>
    )
}

interface FreeDayMessageProps {
    mySchedule: WCalTurnSingleSchedule
}

const FreeDayMessage: React.FC<FreeDayMessageProps> = ({ mySchedule }) => {
    const { t } = useTwinTranslation()
    let type: string= mySchedule.type
    if (mySchedule.isTurnDay) {
        type = 'turnDay'
    }
    const dictFreeDaysTypes: TwinDictionary = {
        'holidayDay': { text: t('exHolidayDay', '¡Día vacaciones!') },
        'freeDay': { text: t('exFreeDay', '¡Día festivo!') },
        'turnDay': { text: t('dayOffDay', '¡Día de descanso!') },
    }
    return (
        <div>
            <div className='rounded-lg border border-green-43 py-8 text-green-43  '>
                <div className='flex justify-center items-center'>
                    <TwinIcon className='h-24 mr-8' icon={faPartyHorn} />
                    <span className='regular18'>
                        {dictFreeDaysTypes[type]?.text || ''}
                    </span>
                </div>
            </div>
        </div>
    )
}

interface HeaderScheduleDayProps extends HeaderScheduleDayLogicProps {
    active: boolean
    punchInWithWorkingCenter: boolean
}

const HeaderScheduleDay: React.FC<HeaderScheduleDayProps> = ({ day, active, mySchedule, setMySchedules, workingCenter, punchInWithWorkingCenter, baseJourney }) => {
    const { t } = useTwinTranslation()
    const { handleActiveDay, onChangeFlexible, onChangeDayOff } = useHeaderScheduleDayLogic({ day, mySchedule, setMySchedules, workingCenter, baseJourney })
    const { days } = useDictDays()
    const myWeekDay = new Date(day).getDay()
    const myDay = new Date(day).getDate()
    const type = mySchedule?.type
    const scheduleDay = mySchedule?.schedule
    const showActiveCheckbox = type !== 'holidayDay' || (type === 'holidayDay' && mySchedule?.isHalfDay) ? true : false
    return (
        <div className='header_schedule_day'>
            <div className='flex items-center mb-15'>
                 <div className='flex items-center mr-auto'>
                    {showActiveCheckbox ? <Checkbox label='' id={"week_day_" + myWeekDay} checked={active} onClick={() => handleActiveDay(active)} /> : null}
                    <label className='regular24 cursor-pointer' htmlFor={"week_day_" + myWeekDay} >{days[myWeekDay].name} {myDay}</label>
                </div>
                {active && scheduleDay?.workingTime?.length && !punchInWithWorkingCenter ? <CheckboxScheduleTurn myWeekDay={myWeekDay} checked={scheduleDay?.flexible} onChange={onChangeFlexible} label={t('flexibleTimetable', 'Horario flexible')} name='flexible' className='mr-10'/> : null}
                {showActiveCheckbox ? <CheckboxScheduleTurn myWeekDay={myWeekDay} checked={!scheduleDay?.workingTime?.length && mySchedule?.isTurnDay && !mySchedule.isBaseTurn} onChange={onChangeDayOff} label={t('dayOff', 'Descanso')} name='dayOff'/> : null}
            </div>
            {scheduleDay?.flexible && active && scheduleDay?.workingTime?.length && !punchInWithWorkingCenter ? <DayFlexibleHours {...{ setMySchedules, day, mySchedule, workingCenter, baseJourney }} /> : null}
        </div>
    )
}

interface HeaderScheduleDayLogicProps extends ScheduleDayBasicProps {}

const useHeaderScheduleDayLogic = ({ day, setMySchedules, workingCenter, baseJourney }: HeaderScheduleDayLogicProps) => {
    const handleActive = useCallback(() => {
        setMySchedules((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            if (!copyOld?.[day]) {
                copyOld[day] = {}
            }
            if (!copyOld?.[day]?.[workingCenter]) {
                const copyGeneralDay = JSON.parse(JSON.stringify(baseJourney[day]))
                copyOld[day][workingCenter] = copyGeneralDay
                copyOld[day][workingCenter].TurnId = undefined
            }
            copyOld[day][workingCenter].isTurnDay = true
            copyOld[day][workingCenter].isBaseTurn = false
             if (!copyOld?.[day]?.[workingCenter]?.schedule?.workingTime?.length) {
                if (!copyOld?.[day]?.[workingCenter]?.schedule) {
                    copyOld[day][workingCenter].schedule = {}
                }
                copyOld[day][workingCenter].schedule.workingTime = copyOld?.[day]?.[workingCenter]?.originalJourney?.workingTime || [[540, 600]]
            }
            return { ...copyOld }
        })
    }, [day, setMySchedules, workingCenter, baseJourney])

    const handleUnactive = useCallback(() => {
        setMySchedules((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld[day][workingCenter].schedule = {
                workingTime: [],
                flexible: false,
                dailyHours: 0
            }
            copyOld[day][workingCenter].isTurnDay = false
            return { ...copyOld }
        })
    }, [day, setMySchedules, workingCenter])

    const handleActiveDay = useCallback((active: boolean) => {
        if (active) {
            handleUnactive()
        } else {
            handleActive()
        }
    }, [handleUnactive, handleActive])

    const onChangeFlexible = useCallback((value: boolean) => {
        setMySchedules((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld[day][workingCenter].schedule ={
                ...copyOld[day][workingCenter].schedule,
                flexible: value,
                dailyHours: value ? copyOld[day][workingCenter].dailyHours : 0
            }
            return { ...copyOld }
        })
    }, [setMySchedules, day, workingCenter])

    const onChangeDayOff = useCallback((value: boolean) => {
        setMySchedules((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            if (!copyOld?.[day]) {
                copyOld[day] = {}
            }
            if (!copyOld?.[day]?.[workingCenter]) {
                const copyGeneralDay = JSON.parse(JSON.stringify(baseJourney[day]))
                copyOld[day][workingCenter] = copyGeneralDay
                copyOld[day][workingCenter].TurnId = undefined
                if (!copyOld?.[day]?.[workingCenter]?.schedule) {
                    copyOld[day][workingCenter].schedule = {}
                }
            }
            copyOld[day][workingCenter].isBaseTurn = false
            copyOld[day][workingCenter].isTurnDay = true
            if (value) {
                copyOld[day][workingCenter].schedule = {
                    ...copyOld[day][workingCenter].schedule,
                    workingTime: [],
                    flexible: false,
                    dailyHours: 0
                }
            } else {
                copyOld[day][workingCenter].schedule.workingTime = [[540, 600]]
            }
            return { ...copyOld }
        })
    }, [setMySchedules, day, workingCenter, baseJourney])

    return { onChangeFlexible, handleActiveDay, onChangeDayOff }
}
interface CheckboxScheduleTurnProps {
    myWeekDay: number
    onChange: (value: boolean) => void
    label: string
    name: string
    className?: string
    checked: boolean
}

const CheckboxScheduleTurn: React.FC<CheckboxScheduleTurnProps> = ({ myWeekDay, onChange, label, name, checked }) => {
    return (
        <Checkbox label={label} id={name + "_" + myWeekDay} className='my-10 ml-10' checked={checked} onChange={onChange} />
    )
}

interface DayFlexibleHoursProps extends DayFlexibleHoursLogicProps { }

const DayFlexibleHours: React.FC<DayFlexibleHoursProps> = ({ day, mySchedule, setMySchedules, workingCenter, baseJourney }) => {
    const { onChangeDailyHours } = useDayFlexibleHoursLogic({ setMySchedules, mySchedule, day, workingCenter, baseJourney })
    const scheduleDay = mySchedule.schedule
    return (
        <div className='items-center justify-end flex'>
            <div className='flex items-center  justify-end my-5'>
                <span>
                    <TwinTrans transKey='dailyHours'>Horas diarias</TwinTrans>
                </span>
                <div className='ml-10 day_flexible_hours'>
                    <RectangularInputHourUnlimited className='to_right' label='' onFinalChange={(value) => onChangeDailyHours(value)} value={String(scheduleDay?.dailyHours || 0)} />
                </div>
            </div>
        </div>
    )
}

interface DayFlexibleHoursLogicProps extends ScheduleDayBasicProps {}

const useDayFlexibleHoursLogic = ({ setMySchedules, day, workingCenter}: DayFlexibleHoursLogicProps) => {
    const onChangeDailyHours = useCallback((mins: number) => {
        setMySchedules((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld[day][workingCenter].schedule['dailyHours'] = mins
            return { ...copyOld }
        })
    }, [setMySchedules, day, workingCenter])

    return { onChangeDailyHours }
}

export default METurnSingleDay